import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Grid, Typography, FormControl, InputLabel, 
  Select, MenuItem, Button, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Checkbox, Chip, Stack
} from '@mui/material';
import { 
  generateTimetable, getDepartments, getSemesters, deleteSectionTimetable
} from '../services/api';
import PageHeader from '../components/PageHeader';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const TimetableGenerator = () => {
  const [departments, setDepartments] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [clearExisting, setClearExisting] = useState(true);
  const [generatedTimetables, setGeneratedTimetables] = useState([]);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [departmentsResponse, semestersResponse] = await Promise.all([
          getDepartments(),
          getSemesters()
        ]);
        setDepartments(departmentsResponse.data);
        setSemesters(semestersResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load data',
          severity: 'error'
        });
      }
    };

    fetchData();
  }, []);

  const handleDepartmentChange = (event) => {
    const departmentId = event.target.value;
    setSelectedDepartment(departmentId);
    // Reset semester selection when department changes
    setSelectedSemester('');
  };

  const handleSemesterChange = (event) => {
    setSelectedSemester(event.target.value);
  };

  const handleCheckboxChange = (event) => {
    setClearExisting(event.target.checked);
  };

  const handleGenerateTimetable = async () => {
    if (!selectedDepartment) {
      setSnackbar({
        open: true,
        message: 'Please select a department',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Get all semesters for the selected department
      const semestersForDepartment = semesters.filter(sem => {
        // If a specific semester is selected, only include that one
        if (selectedSemester) {
          return sem.semester_id === selectedSemester;
        }
        // Otherwise, include all semesters for the department
        return true;
      });
      
      const response = await generateTimetable({
        department_id: selectedDepartment,
        semester_id: selectedSemester || semestersForDepartment[0]?.semester_id,
        clear_existing: clearExisting
      });
      
      // Check if response has the new format with section_timetables
      if (response.data.section_timetables) {
        // New format - directly use the response
        setGeneratedTimetables([response.data]);
      } else {
        // Old format - wrap in array for compatibility
        setGeneratedTimetables([response.data]);
      }
      setSelectedSectionIndex(0);
      
      console.log('Timetable response:', response.data);
      
      // Store generation timestamp in localStorage to trigger refresh in Timetable component
      localStorage.setItem('timetableLastGenerated', new Date().toISOString());
      localStorage.setItem('timetableGeneratedFor', JSON.stringify({
        department_id: selectedDepartment,
        semester_id: selectedSemester || semestersForDepartment[0]?.semester_id
      }));
      
      setSnackbar({
        open: true,
        message: 'Timetable generated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating timetables:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate timetables',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteSectionTimetable = async (section) => {
    if (!selectedDepartment || !selectedSemester) {
      setSnackbar({
        open: true,
        message: 'Department and semester must be selected',
        severity: 'warning'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await deleteSectionTimetable({
        department_id: selectedDepartment,
        semester_id: selectedSemester,
        section: section
      });
      
      console.log('Delete response:', response.data);
      
      // Remove the deleted section from the displayed timetables
      if (generatedTimetables.length > 0 && generatedTimetables[0].section_timetables) {
        const updatedTimetableData = {...generatedTimetables[0]};
        updatedTimetableData.section_timetables = updatedTimetableData.section_timetables.filter(
          sectionData => sectionData.section !== section
        );
        
        if (updatedTimetableData.section_timetables.length > 0) {
          setGeneratedTimetables([updatedTimetableData]);
          setSelectedSectionIndex(0);
        } else {
          setGeneratedTimetables([]);
        }
      }
      
      setSnackbar({
        open: true,
        message: `Timetable for section ${section} deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting timetable:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete timetable',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderGeneratedTimetable = () => {
    console.log('Rendering timetable, selectedSectionIndex:', selectedSectionIndex);
    console.log('generatedTimetables:', generatedTimetables);
    
    if (!generatedTimetables.length) {
      console.log('No timetables to render');
      return null;
    }

    const currentTimetableData = generatedTimetables[0]; // Always use the first timetable data
    console.log('currentTimetableData:', currentTimetableData);
    
    // Check if we have the new format (with section_timetables) or old format
    const isNewFormat = currentTimetableData.section_timetables !== undefined;
    console.log('isNewFormat:', isNewFormat);
    
    if (isNewFormat) {
      // New format with multiple sections
      if (!currentTimetableData.section_timetables || !currentTimetableData.section_timetables.length) {
        console.log('No section timetables available');
        return null;
      }
      
      // Get the selected section timetable
      const sectionIndex = Math.min(selectedSectionIndex, currentTimetableData.section_timetables.length - 1);
      console.log('Calculated sectionIndex:', sectionIndex);
      console.log('Available sections:', currentTimetableData.section_timetables.map(s => s.section));
      
      const sectionTimetable = currentTimetableData.section_timetables[sectionIndex];
      console.log('Selected sectionTimetable:', sectionTimetable);
      
      if (!sectionTimetable) {
        console.error('Section timetable is undefined');
        return null;
      }
      
      if (!sectionTimetable.timetable) {
        console.error('Section timetable has no timetable property');
        return null;
      }
      
      console.log('Section timetable entries:', sectionTimetable.timetable.length);
      
      // Get the selected department details for display
      const selectedDepartmentObj = departments.find(dept => dept.department_id === selectedDepartment);
      
      // Group the timetable entries by day
      const timetableByDay = {};
      sectionTimetable.timetable.forEach(entry => {
        if (!timetableByDay[entry.day_name]) {
          timetableByDay[entry.day_name] = [];
        }
        timetableByDay[entry.day_name].push(entry);
      });

      return (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ mr: 2 }}>
                {selectedDepartmentObj?.department_name || 'Department'} - {currentTimetableData.semester_name}
                {sectionTimetable.section ? ` - Section ${sectionTimetable.section}` : ''} Timetable
              </Typography>
              
              {/* Always show delete button for current section */}
              <Button 
                onClick={() => {
                  if (window.confirm(`Are you sure you want to delete the timetable for Section ${sectionTimetable.section}?`)) {
                    handleDeleteSectionTimetable(sectionTimetable.section);
                  }
                }}
                variant="outlined"
                color="error"
                size="small"
              >
                Delete Section Timetable
              </Button>
            </Box>
            
            {currentTimetableData.section_timetables.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  disabled={sectionIndex === 0}
                  onClick={() => setSelectedSectionIndex(prev => Math.max(0, prev - 1))}
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Previous Section
                </Button>
                <Box sx={{ mx: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Section:
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {currentTimetableData.section_timetables.map((section, idx) => (
                      <Chip
                        key={idx}
                        label={section.section}
                        color={idx === sectionIndex ? "primary" : "default"}
                        variant={idx === sectionIndex ? "filled" : "outlined"}
                        onClick={() => {
                          console.log(`Switching to section ${section.section} (index ${idx})`);
                          console.log('Section timetable:', section);
                          setSelectedSectionIndex(idx);
                        }}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Box>
                <Button 
                  disabled={sectionIndex === currentTimetableData.section_timetables.length - 1}
                  onClick={() => setSelectedSectionIndex(prev => Math.min(currentTimetableData.section_timetables.length - 1, prev + 1))}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Next Section
                </Button>
              </Box>
            )}
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1">
                  Slots Allocated: {sectionTimetable.allocated_slots}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Display timetable by day */}
          {Object.keys(timetableByDay).map(dayName => (
            <Paper key={dayName} sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" gutterBottom>{dayName}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Teacher</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timetableByDay[dayName].map((slot, index) => (
                      <TableRow key={index}>
                        <TableCell>{`${slot.start_time} - ${slot.end_time}`}</TableCell>
                        <TableCell>{`${slot.subject_name} (${slot.subject_code})`}</TableCell>
                        <TableCell>{slot.teacher_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      );
    } else {
      // Legacy format handling (for backward compatibility)
      if (!currentTimetableData.timetable) {
        return null;
      }
      
      // Get the selected department details for display
      const selectedDepartmentObj = departments.find(dept => dept.department_id === selectedDepartment);
      
      // Group the timetable entries by day
      const timetableByDay = {};
      currentTimetableData.timetable.forEach(entry => {
        if (!timetableByDay[entry.day_name]) {
          timetableByDay[entry.day_name] = [];
        }
        timetableByDay[entry.day_name].push(entry);
      });

      return (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedDepartmentObj?.department_name || 'Department'} Timetable
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1">
                  Slots Allocated: {currentTimetableData.allocated_slots}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Display timetable by day */}
          {Object.keys(timetableByDay).map(dayName => (
            <Paper key={dayName} sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" gutterBottom>{dayName}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Teacher</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timetableByDay[dayName].map((slot, index) => (
                      <TableRow key={index}>
                        <TableCell>{`${slot.start_time} - ${slot.end_time}`}</TableCell>
                        <TableCell>{`${slot.subject_name} (${slot.subject_code})`}</TableCell>
                        <TableCell>{slot.teacher_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      );
    }
  };

  return (
    <Box>
      <PageHeader 
        title="Generate Timetable" 
        subtitle="Automatically generate a conflict-free timetable" 
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={handleDepartmentChange}
              >
                {departments.map((department) => (
                  <MenuItem key={department.department_id} value={department.department_id}>
                    {department.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Semester</InputLabel>
              <Select
                value={selectedSemester}
                label="Semester"
                onChange={handleSemesterChange}
              >
                <MenuItem value="">
                  <em>All Semesters</em>
                </MenuItem>
                {semesters
                  .filter(semester => !selectedDepartment || semester.department_id === parseInt(selectedDepartment))
                  .map((semester) => (
                    <MenuItem key={semester.semester_id} value={semester.semester_id}>
                      {semester.semester_name} ({semester.academic_year})
                      {semester.section ? ` - ${semester.section.split(',').length} Sections` : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={clearExisting} 
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="Clear existing timetable"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AutoFixHighIcon />}
              onClick={handleGenerateTimetable}
              disabled={loading}
            >
              Generate Timetable
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {generatedTimetables.length > 0 ? (
            renderGeneratedTimetable()
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                Select a department and semester to generate a timetable
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimetableGenerator;
